<template>
  <div class="crop-elevation-profile">
    <ElevationProfile
      :coordinates="resolvedCoordinates"
      :distances="resolvedDistances"
      :measurement-pref="measurementPref"
      :get-map="getMap"
      :collapsible="true"
      :title="$t('shared.elevation')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type mapboxgl from 'mapbox-gl';
import ElevationProfile from '../../components/ElevationProfile.vue';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';

/**
 * Recording elevation profile for the crop tool.
 *
 * Prefers real `[lng, lat, ele]` coordinates so the shared ElevationProfile can
 * plot on a true cumulative-distance x-axis (equal distance spacing) and colour
 * the line by signed incline/decline gradient. `distances` may be passed as a
 * parallel cumulative-distance array; when coordinates are present it is omitted
 * so distance is derived from geometry via turf (which resets to 0 for a crop).
 *
 * Backward-compat: the legacy `elevations: number[]` prop is still accepted. On
 * that path there is no geometry, so the profile degrades to an index-based
 * x-axis (synthetic distances) and hover -> map sync is unavailable.
 */
const props = withDefaults(
  defineProps<{
    /** Preferred: `[lng, lat, (ele)]` coordinates for the cropped range. */
    coordinates?: number[][] | null;
    /** Optional parallel cumulative-distance array (metres). */
    distances?: number[] | null;
    /** Legacy fallback: plain elevation values (metres). */
    elevations?: number[] | null;
  }>(),
  {
    coordinates: null,
    distances: null,
    elevations: () => [],
  }
);

const measurementPref = MeasurementPrefUtility.preference();

// Map accessor for hover -> map marker sync. The crop map is exposed globally by
// RecordingCropAppMap.vue as `window.recording_crop_app_map`.
const getMap = (): mapboxgl.Map | null =>
  ((window as any).recording_crop_app_map as mapboxgl.Map | undefined) ?? null;

const hasCoordinates = computed(
  () => Array.isArray(props.coordinates) && props.coordinates.length > 0
);

const resolvedCoordinates = computed<number[][]>(() => {
  if (hasCoordinates.value) return props.coordinates as number[][];
  // Legacy fallback: synthesise geometry from bare elevations so the shared
  // builder has something to plot. Coordinates are placeholders (map sync off).
  const elevations = props.elevations ?? [];
  return elevations.map((ele) => [0, 0, ele]);
});

const resolvedDistances = computed<number[] | null>(() => {
  // With real coordinates, let the shared builder compute true distance via turf
  // (correctly restarts at 0 for the cropped slice).
  if (hasCoordinates.value) return props.distances ?? null;
  // Legacy fallback: turf can't derive distance from placeholder [0, 0] coords,
  // so provide an index array. This degrades to an index-based x-axis.
  const elevations = props.elevations ?? [];
  if (elevations.length === 0) return null;
  return elevations.map((_, i) => i);
});
</script>

<style scoped>
.crop-elevation-profile {
  position: absolute;
  bottom: 2em;
  left: 2em;
  right: 2em;
  width: auto;
  margin: 0;
  z-index: 1;
}
</style>
